import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ToasterService } from '../../shared-components/custom-toaster/toaster.service';
import { LoginApiService } from '../../server-api/login-api.service';
import { OperationResult } from '../../models/server-result/operation-result';
import { HttpErrorResponse } from '@angular/common/http';
import { AppContextService } from '../../services/app-context.service';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '../../services/translate/translate.service';
import { LogService } from '../../services/log/log.service';
import { MfaLoginService } from '../../services/authentication/mfa-login.service';
import { ServiceCallStatus } from '../../models/enums/service-call-status';
import { LoginService } from '../../services/authentication/login.service';
import { SessionStatusBroadcastService } from '../../services/authorization/session-status-broadcast.service';
import { BroadcastMessage } from '../../models/broadcast/broadcast-message';
import { BroadcastMessageType } from '../../models/broadcast/broadcast-message-type';
import { GetTokenResult } from '../../models/User/auth/get-token-result';
import { ServerErrors } from '../../models/enums/server-errors';
import { CustomInputComponent } from '../../shared-components/custom-input/custom-input.component';
import { finalize } from 'rxjs/operators';
import dayjs from '../../services/dayjs-setup';

@Component({
	selector: 'app-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
	standalone: false,
})
export class LoginComponent implements OnInit, AfterViewInit {
	@ViewChild('usernameInput') usernameInput!: CustomInputComponent;
	@ViewChild('passwordInput') passwordInput!: CustomInputComponent;

	public username: string;
	public password: string;
	public token: string;
	public isSubmitting: boolean = false;

	public usernameValid: boolean = false;
	public passwordValid: boolean = false;
	public isFormValid: boolean = false;

	private returnUrl: string | null;

	public language: string;

	constructor(
		private loginApiService: LoginApiService,
		private appContext: AppContextService,
		private translateService: TranslateService,
		private toasterService: ToasterService,
		private logger: LogService,
		private route: ActivatedRoute,
		private cdr: ChangeDetectorRef,
		private mfaLoginService: MfaLoginService,
		private loginService: LoginService,
		private sessionStatusBroadcastService: SessionStatusBroadcastService
	) {}

	ngOnInit() {
		// [FLC-588] End any existing session.
		this.appContext.removeSession();
		this.sessionStatusBroadcastService.publish(<BroadcastMessage>{
			type: BroadcastMessageType.SessionKilled,
		});

		this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || null;
		this.route.fragment?.subscribe({
			next: (fragment: string) => {
				if (fragment === ServiceCallStatus.Forbidden) {
					this.alertServiceCallForbidden();
					location.hash = '';
				}
			},
		});
	}

	ngAfterViewInit(): void {
		this.cdr.detectChanges();
	}

	public login(event): void {
		event.preventDefault();

		this.isSubmitting = true;
		this.validateAllFields();

		if (!this.isFormValid) {
			this.isSubmitting = false;
			return;
		}

		this.loginApiService
			.getJwtToken(this.username, this.password)
			.pipe(
				finalize(() => {
					this.isSubmitting = false;
				})
			)
			.subscribe({
				next: (operationResult: OperationResult<GetTokenResult>) => {
					this.processLoginResponse(operationResult);
				},
				error: (httpErrorResponse: HttpErrorResponse) => {
					this.processLoginCallError(httpErrorResponse);
				},
			});
	}

	private processLoginResponse(operationResult: OperationResult<GetTokenResult>) {
		if (operationResult.IsValid) {
			if (operationResult.Data.IsMfaRequired) {
				this.mfaLoginService.handleMfaLoginRequired(this.username, operationResult.Data, this.returnUrl);
			} else {
				this.appContext.storeUserProfileOnLogin(this.username, operationResult.Data);
				this.loginService.getUserInfoOnLoginSuccess(this.username, operationResult.Data, this.returnUrl);
			}
		} else {
			const authProfileSelectionRequiredError = operationResult.Errors?.find(
				(error) => error.Code === ServerErrors.AuthProfileSelectionRequired
			);

			if (!!authProfileSelectionRequiredError) {
				this.loginService.handleAuthPendingProfileSelection(this.username, authProfileSelectionRequiredError, this.returnUrl);
			} else {
				const loginDelayedError = operationResult.Errors?.find((error) => error.Code === ServerErrors.LoginDelayed);

				if (!!loginDelayedError) {
					const currentDelayExpirationValue = loginDelayedError.Values?.[0] || '';
					const currentDelayExpiration: string = !!currentDelayExpirationValue
						? dayjs.utc(Date.parse(currentDelayExpirationValue)).tz(this.appContext.getDefaultTimeZone()).format('YYYY/MM/DD HH:mm:ss')
						: '';
					this.toasterService.showError(
						this.translateService.translate('login.delayed').replace('[EXPIRATION_DATE]', currentDelayExpiration)
					);
				} else {
					this.toasterService.showError(this.translateService.translate('login.invalid.credentials'));
				}
			}
		}
	}

	private processLoginCallError(httpErrorResponse: HttpErrorResponse) {
		if (httpErrorResponse.error instanceof HttpErrorResponse) {
			if (httpErrorResponse.error.status !== 403) {
				//403 is handled by http-error.interceptor
				this.logger.error('[LoginComponent::login] HttpErrorResponse: ', httpErrorResponse);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse, httpErrorResponse.message);
				return;
			}
		}
		this.logger.log(httpErrorResponse.message);
		this.logger.warn('warning: ' + httpErrorResponse.message);
	}

	private alertServiceCallForbidden(): void {
		const msgCouldNotLogin = this.translateService.translate('service.call.forbidden');
		this.toasterService.showError(msgCouldNotLogin);
	}

	onUsernameChange(value: string): void {
		this.username = value;
	}

	onUsernameValidationChange(validation: { isValid: boolean; errors: string[] }): void {
		this.usernameValid = validation.isValid;
		this.updateFormValidity();
	}

	onPasswordChange(value: string): void {
		this.password = value;
	}

	onPasswordValidationChange(validation: { isValid: boolean; errors: string[] }): void {
		this.passwordValid = validation.isValid;
		this.updateFormValidity();
	}

	private updateFormValidity(): void {
		const hasValidationErrors = !this.usernameValid || !this.passwordValid;
		this.isFormValid = !hasValidationErrors;
	}

	private validateAllFields(): void {
		this.usernameInput.validate();
		this.passwordInput.validate();
	}
}
